import {ElevenLabsUtterance} from "./ElevenLabsUtterance";

export declare type ElevenLabsUtteranceCallback = (ev: Event | string, utterance: ElevenLabsUtterance) => void;
export declare type ElevenLabsUtteranceBoundaryCallback = (ev: Event | string, utterance: ElevenLabsUtterance, startOffset: number, length: number) => void;


/**
 *
 * ElevenLabsUtteranceAudioElementRenderer
 *
 * ElevenLabsUtteranceAudioElementRenderer is an implementation of IElevenLabsUtteranceRenderer that uses a
 * HTMLAudioElement to control the playback of the audio data returned by an ElevenLabsUtterance.
 *
 * */
export class ElevenLabsUtteranceAudioElementRenderer {
    private _audioElement = document.createElement('audio');
    private _currentlyPlayingUtterance: ElevenLabsUtterance | undefined;
    private _onUtteranceEndReachedCallback: ElevenLabsUtteranceCallback | undefined;
    private _onPlaybackPausedCallback: ElevenLabsUtteranceCallback | undefined;
    private _onPlaybackStartedCallback: ElevenLabsUtteranceCallback | undefined;
    private _onPlaybackAbortedCallback: ElevenLabsUtteranceCallback | undefined;
    private _onPlaybackErrorCallback: ElevenLabsUtteranceCallback | undefined;
    private _onUtteranceBoundaryCallback: ElevenLabsUtteranceBoundaryCallback | undefined;

    private _timeUpdateTimeoutHandle: number | undefined;

    constructor() {
        this._audioElement.onerror = (ev) => {
            if (this._currentlyPlayingUtterance && this._onPlaybackErrorCallback) {
                this._onPlaybackErrorCallback(ev, this._currentlyPlayingUtterance);
            }
        }

        this._audioElement.onpause = (ev) => {
            if (this._currentlyPlayingUtterance && this._onPlaybackPausedCallback) {
                this._onPlaybackPausedCallback(ev, this._currentlyPlayingUtterance);
            }

        }

        this._audioElement.onabort = (ev) => {
            if (this._currentlyPlayingUtterance && this._onPlaybackAbortedCallback) {
                this._onPlaybackAbortedCallback(ev, this._currentlyPlayingUtterance);
            }

        }

        this._audioElement.onended = (ev) => {
            if (this._currentlyPlayingUtterance) {

                if (this._onUtteranceEndReachedCallback) {
                    this._onUtteranceEndReachedCallback(ev, this._currentlyPlayingUtterance);
                }
            }
        }

        this._audioElement.oncanplaythrough = (_ev) => {
        }

        this._audioElement.oncanplay = (_ev) => {
        }

        this._audioElement.ondurationchange = (_ev) => {
        }

        this._audioElement.onplay = (ev) => {
            if (this._currentlyPlayingUtterance && this._onPlaybackStartedCallback) {
                this._onPlaybackStartedCallback(ev, this._currentlyPlayingUtterance);
            }
        }

        this._audioElement.onplaying = (_ev) => {
        }

        this._audioElement.ontimeupdate = (_ev) => {
            if (this._timeUpdateTimeoutHandle === undefined) {
                this._timeUpdateTimeoutHandle = setTimeout(() => {
                    this._timeUpdateTimeoutHandle = undefined;
                    if (!this._currentlyPlayingUtterance) {
                        return;
                    }
                    if (this._onUtteranceBoundaryCallback) {
                        const timeProgress = this._audioElement.currentTime / this._audioElement.duration;
                        const textProgress = Math.floor(this._currentlyPlayingUtterance.text.value.length * timeProgress);

                        this._onUtteranceBoundaryCallback(_ev, this._currentlyPlayingUtterance, textProgress, 0);
                    }
                }, 500)
            }
        }

        this._audioElement.onseeking = (_ev) => {
        }

        this._audioElement.onseeked = (_ev) => {
        }

        this._audioElement.onratechange = (_ev) => {
        }

        this._audioElement.onprogress = (_ev) => {
        }
    }

    onPlaybackStarted(callback: ElevenLabsUtteranceCallback): void {
        this._onPlaybackStartedCallback = callback;
    }

    onPlaybackPaused(callback: ElevenLabsUtteranceCallback): void {
        this._onPlaybackPausedCallback = callback;
    }

    onPlaybackAborted(callback: ElevenLabsUtteranceCallback): void {
        this._onPlaybackAbortedCallback = callback;
    }

    onPlaybackEndReached(callback: ElevenLabsUtteranceCallback): void {
        this._onUtteranceEndReachedCallback = callback;
    }

    onPlaybackError(callback: ElevenLabsUtteranceCallback): void {
        this._onPlaybackErrorCallback = callback;
    }

    onUtteranceBoundary(callback: ElevenLabsUtteranceBoundaryCallback): void {
        this._onUtteranceBoundaryCallback = callback;
    }

    set playbackRate(rate: number) {
        this._audioElement.playbackRate = rate;
    }

    get playbackRate(): number {
        return this._audioElement.playbackRate;
    }

    get volume(): number {
        return this._audioElement.volume;
    }

    set volume(volume: number) {
        this._audioElement.volume = volume;
    }

    get muted(): boolean {
        return this._audioElement.muted;
    }

    set muted(muted: boolean) {
        this._audioElement.muted = muted;
    }

    getUtterance(): ElevenLabsUtterance | undefined {
        return this._currentlyPlayingUtterance;
    }

    async setUtterance(utterance: ElevenLabsUtterance | undefined): Promise<void> {

        if (!utterance) {
            this.pause();
            this.unset();
            return;
        }

        if (this._audioElement.src && await this._currentlyPlayingUtterance?.fetchHashSignature() === await utterance?.fetchHashSignature()) {
            console.log('ElevenLabsUtteranceRenderer.setUtterance(): Utterance is the same as the currently playing.')
            return;
        }

        this._currentlyPlayingUtterance = utterance;

        await utterance.fetchSpeechData();

        if (!utterance.hasSpeechData) {
            console.warn('ElevenLabsAudioRenderer.utterance: Unable to fetch speech data for utterance.', utterance);
        }

        const blobUrl = utterance.getBlobUrl();
        if (!blobUrl) {
            console.warn('ElevenLabsAudioRenderer.utterance: Unable to get blob url for utterance.', utterance);
            return;
        }

        this._audioElement.src = blobUrl;
    }


    async play(): Promise<void> {
        return this._audioElement.play();
    }

    pause(): void {
        this._audioElement.pause();
    }

    unset(): void {
        this._audioElement.removeAttribute('src');
    }

}
